/*
 * Copyright 2022 Siemens
 */

import React from 'react';
import {Communication} from '../services/communication';
import {Content} from '../common/content';
import {Settings} from '../services/settings';
import {SwwcDefaultCell, SwwcDefaultCellGroup, SwwcIcon} from '@com.siemens.plm.horizon/swwc/bindings/react';
import {CommandBar} from '../common/commandbar';
import App from '../App';

interface DownloadItem {
  name: string;
  category: string;
  description: string;
  file: string;
  type: 'system' | 'tenant';
}

interface CategoryItem<T extends boolean = true> {
  id: string;
  name: string;
  expanded: boolean;
  items: T extends true ? ReadonlyArray<DownloadItem> : Array<DownloadItem>;
}

interface DownloadState {
  isLoading: boolean;
  items: ReadonlyArray<CategoryItem>;
}

export class Download extends React.Component<{}, DownloadState> {
  constructor(props: {}, state: DownloadState) {
    super(props, state);
    this.state = {isLoading: true, items: []};
  }

  override componentDidMount() {
    if (super.componentDidMount) super.componentDidMount();
    this.#reloadData(true);
  }

  #reloadData(skipLoadingUpdate?: true): void {
    if (skipLoadingUpdate === undefined) this.setState({isLoading: true});
    Communication.fetch<ReadonlyArray<DownloadItem>>('downloads', 'GET', 'json+array').then(
      (value) => {
        if (value.status === 'ok' && value.data !== null)
          this.setState({
            isLoading: false,
            items: this.#processDownloadItems(value.data),
          });
        else if (value.status === 'ok') App.toast('warn', 'Download', undefined, `Data of 'downloads' is invalid`);
        else
          App.toast(
            'error',
            'Download',
            undefined,
            `Loading of 'downloads' failed: ${
              ('error' in value && value.error.message) ||
              `[Code: ${value.code}] ${('data' in value && value.data) || 'Unknown'}`
            }`,
          );
      },
      (err) => {
        App.toast(
          'error',
          'Download',
          undefined,
          `Loading of 'downloads' failed: ${(err instanceof Error && err.message) || err}`,
        );
      },
    );
  }

  #category(category: string): {id: string; name: string; expanded: boolean} {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('client'))
      return {
        name: `${Settings.application.NAME.SUITE} ${Settings.application.NAME.CLI} resources`,
        id: 'client',
        expanded: true,
      };
    if (lowerCategory.includes('worker'))
      return {
        name: `${Settings.application.NAME.SUITE} ${Settings.application.NAME.WORKER} resources`,
        id: 'worker',
        expanded: true,
      };
    if (lowerCategory.includes('oss'))
      return {
        name: 'Open source software license information',
        id: 'oss',
        expanded: false,
      };
    if (lowerCategory.includes('docker') || lowerCategory.includes('container'))
      return {name: 'Open source container source code', id: 'docker', expanded: false};
    if (lowerCategory.includes('config')) return {name: 'Configuration', id: 'config', expanded: true};
    if (lowerCategory.includes('doc')) return {name: 'Documentation', id: 'doc', expanded: true};
    if (lowerCategory.includes('manual')) return {name: 'Manual', id: 'man', expanded: true};
    if (lowerCategory.includes('eula')) return {name: 'End-user license agreement', id: 'eula', expanded: false};
    if (lowerCategory.includes('legal')) return {name: 'Legal', id: 'legal', expanded: false};
    if (lowerCategory.includes('privacy') || lowerCategory.includes('gdpr') || lowerCategory.includes('cookie'))
      return {name: 'Privacy', id: 'privacy', expanded: false};
    if (lowerCategory.includes('term') || lowerCategory.includes('tnc') || lowerCategory.includes('t&c'))
      return {name: 'Terms and conditions', id: 't&c', expanded: false};
    return {name: category, id: lowerCategory, expanded: true};
  }

  #processDownloadItems(data: ReadonlyArray<DownloadItem>): ReadonlyArray<CategoryItem> {
    const downloadMap = new Map<string, CategoryItem<false>>();
    for (const item of data) {
      const category = this.#category(item.category);
      const target = downloadMap.get(category.id);
      if (target === undefined)
        downloadMap.set(category.id, {
          ...category,
          items: [item],
        });
      else target.items.push(item);
    }
    return Array.from(downloadMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => {
        item.items.sort((a, b) => a.name.localeCompare(b.name));
        return item as unknown as CategoryItem;
      });
  }

  override render() {
    return (
      <Content
        section='Download'
        isLoading={this.state.isLoading}
      >
        <CommandBar reloadCommand={() => this.#reloadData()}></CommandBar>
        <div className='content'>
          {this.state.items.map((item) => (
            <SwwcDefaultCellGroup
              key={item.id}
              groupTitle={item.name}
              expanded={item.expanded}
              cellDensity='cozy'
            >
              {item.items.map((ele) => (
                <SwwcDefaultCell
                  key={ele.file}
                  cellTitle={ele.name}
                  subTitle={ele.file}
                  description={ele.description}
                >
                  <div slot='right-column-area'>
                    <SwwcIcon
                      name='cmdDownload16'
                      size='lg'
                      title={`Download: ${ele.file}`}
                      onClick={() => (window.location.href = Communication.resolveApi(`downloads/${ele.file}`))}
                    ></SwwcIcon>
                  </div>
                </SwwcDefaultCell>
              ))}
            </SwwcDefaultCellGroup>
          ))}
        </div>
      </Content>
    );
  }
}
